import { Box, lighten, Switch, useTheme } from "@mui/material";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import DocumentsPeriod from "./DocumentsPeriod/DocumentsPeriod";
import { TABLE_BODY_ROW } from "./DocumentsPeriod/utils";
import DocumentsReportCategory from "./DocumentsReportCategory/DocumentsReportCategory";

import HiddenDocTypes from "./DocumentsReportCategory/HiddenDocTypes/HiddenDocTypes";
import { Props, State } from "./interfaces";
import { AllReportsSwitch, ContentContainer } from "./styles";
import { getReportCategoryGroups } from "./utils";
import { SPACING } from "../../_globalStore/redux/reducers/Theme/themeConstants";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import useAuthorization from "../../custom-hooks/useAuthorization/useAuthorization";
import useSearchOptions from "../../custom-hooks/useSearchOptions/useSearchOptions";
import useWindowSize from "../../custom-hooks/useWindowSize/useWindowSize";
import { DocumentTypeReportExclutionDTO } from "../../models/documents/documents-reports/document-type-report-exclusion-dto ";
import { DocumentTypeReportExclutionSTO } from "../../models/documents/documents-reports/document-type-report-exclusion-sto ";
import { saveDocumentTypeReportExclusion } from "../../services/audit-types/document-type-report-exclusion-api.service";
import { markDocumentsVerified } from "../../services/documents/documents-api.service";
import { loadDocumentsReports } from "../../services/documents/documents-reports-api.service";
import {
  FiscalYearCompliancePeriodQuickfilter,
  useFiscalYearCompliancePeriodQuickFilter,
} from "../Quickfilters/FiscalYearCompliancePeriodQuickfilter";

import QuickfiltersContainer from "../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";

import TableHeader from "../TableHeader/TableHeader";

const DocumentsReportTable = ({
  filter,
  documentReportId,
  allReportsAlwaysOn = false,
  loadItemsPath = "/GetDocumentsReport",
  authDomainToHideDoc = null,
  authDomainToSetVerified = null,
  allowAuthorizations = null,
  legalEntityFilter,
  year = new Date().getFullYear(),
  quickFilters,
  tableTitle,
  showPeriodTypeQF = true,
  dynamicColumn,
  screen,
  AmountDetails,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { height } = useWindowSize();

  const allowHere = useAuthorization(allowAuthorizations ?? []);

  const allowedToHideDocType = useAuthorization([authDomainToHideDoc ?? ""]);
  const allowedToSetVerified = useAuthorization([authDomainToSetVerified ?? ""]);

  const { sidebarShow } = useAppSelector((state) => state.appReducer);

  const { fiscalYearCompliancePeriodQuickFilter, setFiscalYearCompliancePeriodQuickFilter } =
    useFiscalYearCompliancePeriodQuickFilter();

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions(filter);

  const headerDiv = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const [allReports, setAllReports] = useState<boolean>(allReportsAlwaysOn);
  const [state, setState] = useState<State>({
    items: [],
    loading: false,
    error: "",
  });

  const loadData = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const { error, items } = await loadDocumentsReports(
          searchOptions,
          documentReportId,
          year,
          allReports,
          legalEntityFilter?.legalEntityOption?.legalEntityIds,
          legalEntityFilter?.legalEntityOption?.withoutLegalEntity,
          fiscalYearCompliancePeriodQuickFilter.period.periodTypes,
          loadItemsPath,
          signal
        );
        const reportCategories = items[0]?.reportCategories ?? [];

        if (!signal || !signal.aborted) {
          setState((state) => ({
            ...state,
            items: reportCategories,
            error: error ?? "",
          }));
        }
      } catch (error) {
        if (!signal || !signal.aborted) {
          setState((state) => ({ ...state, error: error as string, items: [] }));
        }
      } finally {
        if (!signal || !signal.aborted) {
          setState((state) => ({ ...state, loading: false }));
        }
      }
    },
    [
      searchOptions,
      documentReportId,
      year,
      allReports,
      legalEntityFilter?.legalEntityOption?.legalEntityIds,
      legalEntityFilter?.legalEntityOption?.withoutLegalEntity,
      fiscalYearCompliancePeriodQuickFilter.period.periodTypes,
      loadItemsPath,
    ]
  );

  const markAsVerified = useCallback(
    async (documentIDs: string[], signal?: AbortSignal): Promise<void> => {
      if (!allowedToSetVerified) return;

      setState((state) => ({ ...state, loading: true }));

      try {
        await markDocumentsVerified(signal, documentIDs);
        await loadData();
      } catch (error) {
        if (!signal || !signal.aborted) {
          setState((state) => ({ ...state, error: error as string }));
        }
      } finally {
        if (!signal || !signal.aborted) {
          setState((state) => ({ ...state, loading: false }));
        }
      }
    },
    [loadData, allowedToSetVerified]
  );

  const hideRowFromReport = useCallback(
    async (documentTypeId: string): Promise<void> => {
      if (!allowedToHideDocType) return;

      setState((state) => ({ ...state, loading: true }));

      try {
        const documentTypeReportExclution = {
          id: uuidv4(),
          legalEntity: {
            id: legalEntityFilter?.legalEntityOption?.legalEntityIds[0],
          },
          report: { id: documentReportId }, // AuditTypeReportRef
          documentType: { id: documentTypeId }, // DocumentTypeRef
        };

        const response = await saveDocumentTypeReportExclusion(
          documentTypeReportExclution as DocumentTypeReportExclutionSTO
        );

        await loadData();

        setState((state) => ({
          ...state,

          error: response.error ?? "",
        }));
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [documentReportId, legalEntityFilter, loadData, allowedToHideDocType]
  );

  const unhideRowFromReport = useCallback(
    async (excludedDocType: DocumentTypeReportExclutionDTO): Promise<void> => {
      if (!allowedToHideDocType) return;

      setState((state) => ({ ...state, loading: true }));

      try {
        const documentTypeReportExclution = {
          ...excludedDocType,
          isDeleted: true,
        };

        const response = await saveDocumentTypeReportExclusion(
          documentTypeReportExclution as DocumentTypeReportExclutionSTO
        );

        await loadData();

        setState((state) => ({
          ...state,

          error: response.error ?? "",
        }));
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [documentReportId, legalEntityFilter, loadData, allowedToHideDocType]
  );

  const askAction = (
    action: "markAllVerified" | "hideLine" | "unhideLine",
    docIDs: string[],
    docTypeId: string,
    excludedDocType?: DocumentTypeReportExclutionDTO
  ) => {
    const abortController = new AbortController();

    switch (action) {
      case "markAllVerified":
        if (allowedToSetVerified && window.confirm("Mark row documents as verified?")) {
          markAsVerified(docIDs, abortController.signal);
        }
        break;
      case "hideLine":
        if (allowedToHideDocType && window.confirm("Hide forever this line from report?")) {
          hideRowFromReport(docTypeId);
        }
        break;
      case "unhideLine":
        if (allowedToHideDocType && excludedDocType && window.confirm("Show this line in report?")) {
          unhideRowFromReport(excludedDocType);
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    timer = setTimeout(() => loadData(abortController.signal), searchOptions.query ? 1000 : 0);

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [searchOptions, loadData]);

  const currHeight =
    height -
    (SPACING[9] * 2 +
      (document.getElementById("header")?.clientHeight ?? 0) +
      SPACING[7] +
      (document.getElementById("tableHeader")?.clientHeight ?? 0) +
      SPACING[7] +
      (document.getElementById("MultitabsHorizontal-Portal")?.clientHeight ?? 0) +
      SPACING[7]);

  useEffect(() => {
    if (headerDiv.current) {
      setHeaderHeight(headerDiv.current?.clientHeight ?? 0);
    }
  }, [state.items]);

  return (
    <LoadingOverlay
      loading={state.loading}
      spinnerSize="4x"
    >
      <>
        {allowHere && (
          <ContentContainer>
            <TableHeader
              searchOptions={searchOptions}
              handleChangeSearchOptions={handleChangeSearchOptions}
              tableTitle={tableTitle}
              quickFilters={
                <QuickfiltersContainer>
                  <>
                    {!!quickFilters && quickFilters}
                    {showPeriodTypeQF && (
                      <FiscalYearCompliancePeriodQuickfilter
                        quickFilters={fiscalYearCompliancePeriodQuickFilter}
                        handleChangeQuickFilters={setFiscalYearCompliancePeriodQuickFilter}
                      />
                    )}
                    {allowedToHideDocType && (
                      <HiddenDocTypes
                        reportId={documentReportId}
                        legalEntityIds={legalEntityFilter?.legalEntityOption?.legalEntityIds ?? []}
                        handleNotifyParent={() => loadData()}
                        doUnhideDocType={(excludedDocType: DocumentTypeReportExclutionDTO) => {
                          askAction("unhideLine", [], "", excludedDocType);
                        }}
                      />
                    )}
                    {!allReportsAlwaysOn && (
                      <AllReportsSwitch
                        label={`${t(".document_reports.all_reports_label")}`}
                        control={
                          <Switch
                            checked={allReports}
                            onChange={(_, checked) => setAllReports(checked)}
                          />
                        }
                      />
                    )}
                  </>
                </QuickfiltersContainer>
              }
            />
            <Box
              sx={{
                minWidth: "800px",
                height: `${currHeight}px`,
                overflow: "auto",
                zIndex: 1,

                [`& .${TABLE_BODY_ROW}`]: {
                  borderBottom: `1px solid ${lighten(theme.palette.secondary.light, 0.6)}`,
                  minHeight: "60px",
                },

                "& p": {
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                },
              }}
            >
              <Box
                ref={headerDiv}
                sx={{
                  width: "100%",
                  position: "sticky",
                  top: 0,
                  zIndex: 1000,
                  background: "white",
                }}
              >
                <DocumentsPeriod
                  items={state.items}
                  dynamicColumn={dynamicColumn?.name}
                />
              </Box>

              {state.items.map(({ id, reportCategoryName, reportCategoryCode }, idx) => (
                <DocumentsReportCategory
                  key={id}
                  reportCategoryGroups={getReportCategoryGroups(state.items, id)}
                  categoryName={reportCategoryName}
                  categoryCode={reportCategoryCode}
                  handleNotifyTable={() => loadData()}
                  legalEntity={legalEntityFilter?.selectedLegalEntity}
                  reportYear={year}
                  askAction={askAction}
                  askHide={allowedToHideDocType}
                  askVerified={allowedToSetVerified}
                  dynamicColumn={dynamicColumn}
                  screen={screen}
                  AmountDetails={AmountDetails}
                  itemIdx={idx}
                  topForStickyRows={headerHeight}
                />
              ))}
            </Box>
          </ContentContainer>
        )}
      </>
    </LoadingOverlay>
  );
};

export default DocumentsReportTable;
